import tinycolor from 'tinycolor2';
import DOMPurify from 'dompurify';
import { WidgetPort } from '@kemu-io/hs-types';
import { PortDescription } from './index';


/**
 * Dynamically builds a list of ports and their position based on the number of ports
 * @param ports The list of ports to build the description for
 * @param customSpacing The space between ports, defaults is 0.12.
 */
export const buildOutputPortsDescription = (ports: WidgetPort[], customSpacing?: number): PortDescription[] => {
  const outputFraction = ((1 - (customSpacing ?? 0.12)) / ports.length);
  const outputs = ports.map((output, i) => {
    let space = (outputFraction * (i + 1));
    if (ports.length === 1) { space = 0.5; }
    if (ports.length === 2) { space = 0.4 + (0.33 * i); }

    // reduce padding on the first port
    space -= 0.05;


    const description: PortDescription =  {
      name: output.name,
      type: output.type,
      jsonShape: output.jsonShape,
      position: [1, space, 1, 0]
    };

    return description;
  });

  return outputs;
};

/**
 * Finds the ideal "complementary colors" for a given color
 */
export const calculateWidgetColors = (color: string) => {
  const brightness = tinycolor(color).getBrightness();
  const complement = tinycolor(color).complement().toString();
  const isBrightColor = brightness > 200;
  const content = tinycolor(color).lighten(4).toString();
  // Finds a text color that is legible on the background color
  const text = tinycolor.mostReadable(content, ['#333', '#fff'], {
    includeFallbackColors: true,
    level: 'AA',
    size: 'large',
  }).toString();

  const header = isBrightColor
    ? tinycolor(color).darken(10).toString()
    : color;

  const border = header;

  return {
    content,
    header,
    border,
    brightness,
    isBrightColor,
    complement,
    text,
  };
};

/**
 * Checks if a string is an SVG string (starts with <svg or contains SVG content)
 * @param str The string to check
 * @returns true if the string is an SVG string, false otherwise
 */
export const isSvgString = (str: string | undefined): boolean => {
  if (!str) {
    return false;
  }
  const trimmed = str.trim();
  return trimmed.startsWith('<svg') || trimmed.startsWith('<?xml');
};

/**
 * Validates that a string contains valid SVG content
 * @param svgStr The SVG string to validate
 * @returns true if the string is valid SVG, false otherwise
 */
export const validateSvgContent = (svgStr: string): boolean => {
  if (!svgStr || typeof svgStr !== 'string') {
    return false;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgStr, 'image/svg+xml');
    const svgElement = doc.documentElement;

    // Check for parsing errors
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      return false;
    }

    // Verify it's actually an SVG element
    return svgElement !== null && svgElement.nodeName === 'svg';
  } catch {
    return false;
  }
};

/**
 * Sanitizes an SVG string to prevent XSS attacks
 * Removes potentially dangerous content like scripts and event handlers
 * @param svgStr The SVG string to sanitize
 * @returns The sanitized SVG string
 */
export const sanitizeSvg = (svgStr: string): string => {
  if (!svgStr || typeof svgStr !== 'string') {
    return '';
  }

  try {
    // DOMPurify configuration for SVG - allow SVG elements but remove scripts and event handlers
    const clean = DOMPurify.sanitize(svgStr, {
      USE_PROFILES: { svg: true, svgFilters: true },
      KEEP_CONTENT: true,
      ADD_ATTR: ['viewBox', 'xmlns'],
    });
    return clean;
  } catch (error) {
    console.warn('Failed to sanitize SVG:', error);
    return '';
  }
};

/**
 * Error keys for translation
 */
export const SvgErrorKey = {
  FILE_SIZE: 'FileSizeError',
  INVALID_SVG: 'InvalidSvgError',
  SANITIZE_FAILED: 'SanitizeError',
  PROCESS_ERROR: 'ProcessError',
} as const;

/**
 * Error handler callback type
 */
export type SvgErrorHandler = (errorKey: typeof SvgErrorKey[keyof typeof SvgErrorKey]) => void;

/**
 * Validates and sanitizes an SVG file from Uint8Array
 * This is a reusable utility function that handles all validation and sanitization
 * @param data The file data as Uint8Array
 * @param maxFileSize Maximum file size in bytes
 * @param onError Callback to handle errors (e.g., show error message)
 * @returns The sanitized SVG string if successful, null otherwise
 */
export const processSvgFile = (
  data: Uint8Array,
  maxFileSize: number,
  onError: SvgErrorHandler
): string | null => {
  // Validate file size
  if (data.length > maxFileSize) {
    onError(SvgErrorKey.FILE_SIZE);
    return null;
  }

  try {
    // Decode the file content
    const decoder = new TextDecoder('utf-8');
    const svgContent = decoder.decode(data);

    // Validate that the content is valid SVG
    if (!validateSvgContent(svgContent)) {
      onError(SvgErrorKey.INVALID_SVG);
      return null;
    }

    // Sanitize the SVG to prevent XSS attacks
    const sanitizedSvg = sanitizeSvg(svgContent);
    if (!sanitizedSvg) {
      onError(SvgErrorKey.SANITIZE_FAILED);
      return null;
    }

    return sanitizedSvg;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error processing SVG file:', errorMessage);
    onError(SvgErrorKey.PROCESS_ERROR);
    return null;
  }
};

/**
 * Adds white fill color to an SVG string if no fill is defined
 * This is used for preview purposes where icons should be white on colored backgrounds
 * @param svgStr The SVG string to modify
 * @returns The modified SVG string with white fill (if no fill was defined)
 * @throws {Error} If the SVG string cannot be parsed
 */
export const addWhiteFillToSvg = (svgStr: string): string => {
  if (!svgStr) {
    return svgStr;
  }

  try {
    // Parse the SVG string
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgStr, 'image/svg+xml');
    const svgElement = doc.documentElement;

    if (!svgElement || svgElement.nodeName !== 'svg') {
      return svgStr;
    }

    // Check if SVG has a fill attribute - only apply white if no fill is defined
    const svgFill = svgElement.getAttribute('fill');
    if (!svgFill || svgFill === 'none' || svgFill === 'currentColor') {
      svgElement.setAttribute('fill', 'white');
    }

    // Process all child elements - only apply white if no fill is defined
    const allElements = svgElement.querySelectorAll('*');
    allElements.forEach((element) => {
      const elementFill = element.getAttribute('fill');
      if (!elementFill || elementFill === 'none' || elementFill === 'currentColor') {
        element.setAttribute('fill', 'white');
      }
    });

    // Serialize back to string
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgElement);
  } catch (error: unknown) {
    // If parsing fails, return original string
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.warn('Failed to add white fill to SVG:', errorMessage);
    return svgStr;
  }
};
